import type { Lang } from '@shared/types';

/** Минимальный интерфейс над localStorage — чтобы подменять в тестах и на платформах без него. */
export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const PREFIX = 'tp4_';
/** Ключи, которые имеет смысл унести из прошлой версии (без сервера, префикс tp3_). */
const LEGACY_PREFIX = 'tp3_';
const LEGACY_KEYS = ['lang', 'seenHelp'];
const LEGACY_PATTERNS = [/^last_/];
/**
 * Рекорды прошлых версий. Нагрев «от услышанной бомбы» вернулся (решение №36), поэтому:
 *  - tp3_best_* (прототип) и tp4_best_* (первая версия) считались по ТЕМ ЖЕ правилам — числа совместимы, возвращаем их как рекорды;
 *  - tp4_best2_* (период «шкала только от обманок») — шкала другая, числа несравнимы: остаётся лишь факт «пройдено».
 */
const COMPARABLE_RECORD = /^tp[34]_best_(.+)$/;
const INCOMPARABLE_RECORD = /^tp4_best2_(.+)$/;

/** Итог игрока по полю: heat — лучший (минимальный) нагрев бомбы, null — пройдено давно, результат не сохранился. */
export interface BoardProgress {
  heat: number | null;
}

/**
 * Локальные данные игрока: язык, рекорды, последнее поле, текущая партия.
 * Любое обращение к хранилищу может бросить (приватный режим, квота) — тогда молча работаем без него.
 */
export class Persist {
  constructor(private readonly storage: KeyValueStorage | null) {}

  get<T>(key: string, fallback: T): T {
    try {
      const raw = this.storage?.getItem(PREFIX + key);
      return raw == null ? fallback : (JSON.parse(raw) as T);
    } catch {
      return fallback;
    }
  }

  set(key: string, value: unknown): void {
    try {
      this.storage?.setItem(PREFIX + key, JSON.stringify(value));
    } catch {
      // квота или запрет — не критично
    }
  }

  /**
   * Разовая миграция с tp3_ (прототип без сервера): переносим язык, рекорды и «последнее поле».
   * Ключ OpenRouter и сгенерированные поля не переносим — их больше нет на клиенте.
   * Возвращает true, если миграция выполнялась сейчас.
   */
  migrateLegacy(keys: readonly string[]): boolean {
    if (this.get('migratedFromTp3', false)) return false;
    for (const short of keys) {
      if (!LEGACY_KEYS.includes(short) && !LEGACY_PATTERNS.some((re) => re.test(short))) continue;
      try {
        const raw = this.storage?.getItem(LEGACY_PREFIX + short);
        if (raw != null && this.storage?.getItem(PREFIX + short) == null)
          this.storage?.setItem(PREFIX + short, raw);
      } catch {
        // пропускаем
      }
    }
    this.set('migratedFromTp3', true);
    return true;
  }

  getLang = (): Lang => (this.get<Lang>('lang', 'ru') === 'en' ? 'en' : 'ru');
  setLang = (lang: Lang): void => this.set('lang', lang);
  seenHelp = (): boolean => this.get('seenHelp', false);
  markHelpSeen = (): void => this.set('seenHelp', true);
  getLastBoard = (lang: Lang): string | null => this.get<string | null>(`last_${lang}`, null);
  setLastBoard = (lang: Lang, id: string): void => this.set(`last_${lang}`, id);
  getBest = (boardId: string): number | null => this.get<number | null>(`best3_${boardId}`, null);
  /** Запоминает рекорд (минимальный нагрев); возвращает true, если побит. */
  saveBest(boardId: string, heat: number): boolean {
    const prev = this.getBest(boardId);
    if (prev != null && prev <= heat) return false;
    this.set(`best3_${boardId}`, heat);
    return true;
  }
  /** Пройдено ли поле (хотя бы одна победа) и с каким лучшим нагревом. null — ещё не пройдено. */
  getProgress(boardId: string): BoardProgress | null {
    const heat = this.getBest(boardId);
    if (heat != null) return { heat };
    return this.get(`passed_${boardId}`, false) ? { heat: null } : null;
  }

  /**
   * Разовый перенос рекордов прошлых версий (см. COMPARABLE_RECORD / INCOMPARABLE_RECORD).
   * `allKeys` — ключи хранилища как есть, с префиксами. Возвращает число затронутых полей.
   */
  migrateOldRecords(allKeys: readonly string[]): number {
    if (this.get('records3', false)) return 0;
    let moved = 0;
    for (const key of allKeys) {
      const comparable = COMPARABLE_RECORD.exec(key)?.[1];
      const other = INCOMPARABLE_RECORD.exec(key)?.[1];
      if (comparable) {
        const heat = this.readNumber(key);
        if (heat != null) this.saveBest(comparable, heat);
        else this.set(`passed_${comparable}`, true);
        moved++;
      } else if (other && !this.getProgress(other)) {
        this.set(`passed_${other}`, true);
        moved++;
      }
    }
    this.set('records3', true);
    return moved;
  }

  private readNumber(fullKey: string): number | null {
    try {
      const v = JSON.parse(this.storage?.getItem(fullKey) ?? 'null');
      return typeof v === 'number' && Number.isFinite(v) ? v : null;
    } catch {
      return null;
    }
  }

  getGameId = (boardId: string): string | null => this.get<string | null>(`game_${boardId}`, null);
  setGameId = (boardId: string, gameId: string | null): void => this.set(`game_${boardId}`, gameId);
}

/** localStorage, если он доступен (доступ сам по себе может бросить исключение). */
export function browserStorage(): KeyValueStorage | null {
  try {
    return (globalThis as { localStorage?: KeyValueStorage }).localStorage ?? null;
  } catch {
    return null;
  }
}

/** Все ключи хранилища как есть (с префиксами) — для миграции старых рекордов. */
export function storageKeys(
  storage: (KeyValueStorage & { length: number; key(i: number): string | null }) | null,
): string[] {
  const out: string[] = [];
  try {
    for (let i = 0; storage && i < storage.length; i++) {
      const k = storage.key(i);
      if (k) out.push(k);
    }
  } catch {
    // ignore
  }
  return out;
}

/** Ключи localStorage без префикса приложения — для миграции. */
export function legacyKeys(
  storage: (KeyValueStorage & { length: number; key(i: number): string | null }) | null,
): string[] {
  const out: string[] = [];
  try {
    for (let i = 0; storage && i < storage.length; i++) {
      const k = storage.key(i);
      if (k?.startsWith(LEGACY_PREFIX)) out.push(k.slice(LEGACY_PREFIX.length));
    }
  } catch {
    // ignore
  }
  return out;
}
